import { api } from '@/lib/api'

export interface Session {
  id: string
  studentId: string
  counselorId: string
  studentName: string
  studentGrade?: string
  type: 'career_guidance' | 'college_prep' | 'academic_support' | 'personal_counseling'
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show'
  startedAt: string
  endedAt?: string
  scheduledAt?: string
  duration: number
  rating?: number
  feedback?: string
  summary?: string
  notes?: string
  topics: string[]
  followUpRequired: boolean
  nextSessionScheduled?: string
  paymentStatus: 'paid' | 'pending' | 'refunded'
  amount?: number
  createdAt: string
  updatedAt: string
}

export interface SessionRequest {
  id: string
  studentId: string
  studentName: string
  type: 'career_guidance' | 'college_prep' | 'academic_support' | 'personal_counseling'
  preferredDate: string
  preferredTime: string
  description: string
  urgency: 'low' | 'medium' | 'high' | 'urgent'
  status: 'pending' | 'approved' | 'rejected' | 'cancelled'
  createdAt: string
}

export interface UpdateSessionStatusDto {
  status: 'approved' | 'rejected' | 'completed' | 'cancelled' | 'no_show'
  notes?: string
  summary?: string
  rating?: number
  followUpRequired?: boolean
  scheduledAt?: string
}

// Get counselor's sessions
export const getCounselorSessions = async (params?: {
  status?: string
  type?: string
  startDate?: string
  endDate?: string
  studentId?: string
  page?: number
  limit?: number
}): Promise<{ sessions: Session[], total: number, page: number, limit: number }> => {
  const queryParams = { role: 'counselor', ...params }
  return api.get('/sessions/my-sessions', queryParams)
}

// Get specific session details
export const getSessionDetails = async (sessionId: string): Promise<Session> => {
  return api.get<Session>(`/sessions/${sessionId}`)
}

// Update session status (approve/reject/complete)
export const updateSessionStatus = async (
  sessionId: string,
  data: UpdateSessionStatusDto
): Promise<Session> => {
  return api.put<Session>(`/sessions/${sessionId}/status`, data)
}

// Get session requests for counselor
export const getSessionRequests = async (userId?: string, params?: {
  status?: 'pending' | 'approved' | 'rejected'
  urgency?: 'low' | 'medium' | 'high' | 'urgent'
  page?: number
  limit?: number
}): Promise<{ requests: SessionRequest[], total: number }> => {
  if (!userId) {
    throw new Error('User ID is required to fetch session requests')
  }
  return api.get(`/profile/counselor/pending-requests/${userId}`, params)
}

// Approve session request
export const approveSessionRequest = async (
  requestId: string,
  data: {
    scheduledAt: string
    notes?: string
  }
): Promise<Session> => {
  return api.patch<Session>(`/sessions/requests/${requestId}/approve`, data)
}

// Reject session request
export const rejectSessionRequest = async (
  requestId: string,
  reason: string
): Promise<void> => {
  return api.patch<void>(`/sessions/requests/${requestId}/reject`, { reason })
}